import 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/snippets/python';
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/mode-javascript";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/mode-css";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/mode-markdown";
import "ace-builds/src-noconflict/mode-plain_text";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/ext-language_tools";

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

import AceEditor from "react-ace";
import Sidebar from "../nav/Sidebar";
import { SidebarTab } from "../../../types";
import TableViewer from "./TableViewer";
import VersionBanner from "../ui/VersionBanner";
import { cn } from "../../lib/utils";
import { debounce } from "lodash";
import { filesystemService } from "../../utils/filesystem";
import { getLanguageFromExtension } from "../../lib/fileUtils";
import { observer } from "mobx-react-lite";
import { rootStore } from "../../stores/RootStore";

interface CodeEditorProps {
    selectedFile: string | null;
    className?: string;
    appLoading: boolean;
    isGenerating?: boolean;
    rootFolder?: string | null;
    isPyodideLoaded?: boolean;
    setSelectedFile: (path: string) => void;
    showSidebar?: boolean;
}

interface EditorInstance {
    undo: () => void;
    redo: () => void;
    session: {
        getUndoManager: () => {
            hasUndo: () => boolean;
            hasRedo: () => boolean;
        };
        on: (name: 'change', callback: () => void) => void;
        off: (name: 'change', callback: () => void) => void;
    };
}

export interface CodeEditorRef {
    refresh: () => Promise<void>;
    undo: () => void;
    redo: () => void;
    canUndo: () => boolean;
    canRedo: () => boolean;
    getCurrentContent: () => string;
    updateFromVersion: (content: string) => void;
}

const CodeEditor = observer(forwardRef<CodeEditorRef, CodeEditorProps>(({
    selectedFile,
    className,
    appLoading,
    isGenerating = false,
    rootFolder = null,
    isPyodideLoaded = false,
    setSelectedFile,
    showSidebar = true
}, ref) => {
    const { uiStore, projectStore } = rootStore;
    const [code, setCode] = useState<string>("");
    const [fileType, setFileType] = useState<"text" | "image" | "csv" | null>(null);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [language, setLanguage] = useState<string>("plaintext");
    const [error, setError] = useState<string | null>(null);
    const [editorKey, setEditorKey] = useState<number>(0);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeTab, setActiveTab] = useState<SidebarTab>(SidebarTab.Editor);
    const [editor, setEditor] = useState<EditorInstance | null>(null);
    const [isVersionUpdate, setIsVersionUpdate] = useState(false);

    const isHelloPy = selectedFile === "/project/hello.py";

    const pyodidePath = useMemo(() => {
        if (!selectedFile) return null;
        return selectedFile.startsWith('/') ? selectedFile : `/project/${selectedFile}`;
    }, [selectedFile]);

    const filename = useMemo(() => {
        if (!selectedFile) return "";
        return selectedFile.split("/").pop() || "";
    }, [selectedFile]);

    const editorOptions = useMemo(() => ({
        fontSize: 13,
        showLineNumbers: false,
        showGutter: false,
        highlightActiveLine: false,
        showPrintMargin: false,
        useSoftTabs: true,
        tabSize: 4,
        enableBasicAutocompletion: true,
        enableLiveAutocompletion: true,
        enableSnippets: true,
        theme: "github",
        wrap: true,
        wrapMethod: "free",
        useWrapMode: true,
    }), []);

    const saveFile = useMemo(() =>
        debounce(async (content: string) => {
            if (!pyodidePath) return;

            try {
                setError(null);
                await filesystemService.writeFileToPyodide(pyodidePath, content);

                if (isHelloPy && !isVersionUpdate) {
                    await uiStore.checkForChanges(content);
                }
            } catch (error) {
                console.error("Error saving file:", error);
                setError(`Error saving file: ${error instanceof Error ? error.message : String(error)}`);
            }
        }, 1000),
        [pyodidePath, isHelloPy, isVersionUpdate]
    );

    const saveCSVFile = useMemo(() => async (content: string) => {
        if (!pyodidePath) return;

        try {
            setError(null);
            await filesystemService.writeFileToPyodide(pyodidePath, content);
        } catch (error) {
            console.error("Error saving file:", error);
            setError(`Error saving file: ${error instanceof Error ? error.message : String(error)}`);
        }
    }, [pyodidePath]);

    const handleCodeChange = useCallback((value: string | undefined) => {
        if (!value || isVersionUpdate) return;
        setCode(value);

        if (fileType === "csv") {
            saveCSVFile(value);
        } else {
            saveFile(value);
        }
    }, [fileType, saveCSVFile, saveFile, isVersionUpdate]);

    const refreshFile = useCallback(async () => {
        if (!pyodidePath || appLoading) return;

        try {
            const content = await filesystemService.readFileFromPyodide(pyodidePath);

            if (content !== code) {
                setCode(content);
                setEditorKey(prevKey => prevKey + 1);
            }
        } catch (error) {
            console.error("Error refreshing file:", error);
            setError(`Error refreshing file: ${error instanceof Error ? error.message : String(error)}`);
        }
    }, [pyodidePath, appLoading, code]);

    const handleUndo = useCallback(() => {
        if (!isHelloPy) return;

        const content = uiStore.undo();
        if (content !== null) {
            setIsVersionUpdate(true);
            setCode(content);
            setEditorKey(prevKey => prevKey + 1);

            setTimeout(async () => {
                if (pyodidePath) {
                    await filesystemService.writeFileToPyodide(pyodidePath, content);
                }
                setIsVersionUpdate(false);
            }, 100);
        }
    }, [isHelloPy, pyodidePath]);

    const handleRedo = useCallback(() => {
        if (!isHelloPy) return;

        const content = uiStore.redo();
        if (content !== null) {
            setIsVersionUpdate(true);
            setCode(content);
            setEditorKey(prevKey => prevKey + 1);

            setTimeout(async () => {
                if (pyodidePath) {
                    await filesystemService.writeFileToPyodide(pyodidePath, content);
                }
                setIsVersionUpdate(false);
            }, 100);
        }
    }, [isHelloPy, pyodidePath]);

    const handleRestoreVersion = useCallback(async () => {
        if (!isHelloPy) return;

        const content = await uiStore.restoreCurrentVersion();
        if (content !== null) {
            setIsVersionUpdate(true);
            setCode(content);
            setEditorKey(prevKey => prevKey + 1);

            setTimeout(async () => {
                if (pyodidePath) {
                    await filesystemService.writeFileToPyodide(pyodidePath, content);
                }
                setIsVersionUpdate(false);
            }, 100);
        }
    }, [isHelloPy, pyodidePath]);

    const handleBackToLatest = useCallback(async () => {
        if (!isHelloPy) return;

        const content = await uiStore.goToLatestVersion();
        if (content !== null) {
            setIsVersionUpdate(true);
            setCode(content);
            setEditorKey(prevKey => prevKey + 1);

            setTimeout(async () => {
                if (pyodidePath) {
                    await filesystemService.writeFileToPyodide(pyodidePath, content);
                }
                setIsVersionUpdate(false);
            }, 100);
        }
    }, [isHelloPy, pyodidePath]);

    const handleSaveChangesAsLatest = useCallback(async () => {
        if (!isHelloPy) return;

        const content = await uiStore.saveModifiedPreviousVersionAsLatest();
        if (content !== null) {
            setIsVersionUpdate(true);
            setCode(content);
            setEditorKey(prevKey => prevKey + 1);

            setTimeout(async () => {
                if (pyodidePath) {
                    await filesystemService.writeFileToPyodide(pyodidePath, content);
                }
                setIsVersionUpdate(false);
            }, 100);
        }
    }, [isHelloPy, pyodidePath]);

    const handleDiscardChanges = useCallback(async () => {
        if (!isHelloPy) return;

        const content = await uiStore.discardChangesInPreviousVersion();
        if (content !== null) {
            setIsVersionUpdate(true);
            setCode(content);
            setEditorKey(prevKey => prevKey + 1);

            setTimeout(async () => {
                if (pyodidePath) {
                    await filesystemService.writeFileToPyodide(pyodidePath, content);
                }
                setIsVersionUpdate(false);
            }, 100);
        }
    }, [isHelloPy, pyodidePath]);

    useEffect(() => {
        const handleVersionCheck = async () => {
            if (isHelloPy && !isVersionUpdate) {
                await uiStore.checkForChanges(code);
            }
        };

        const handleVersionCheckSync = () => {
            handleVersionCheck().catch(console.error);
        };

        window.addEventListener('version:check', handleVersionCheckSync);
        return () => window.removeEventListener('version:check', handleVersionCheckSync);
    }, [isHelloPy, code, isVersionUpdate]);

    const updateFromVersion = useCallback((content: string) => {
        setIsVersionUpdate(true);
        setCode(content);
        setEditorKey(prevKey => prevKey + 1);

        setTimeout(async () => {
            if (pyodidePath) {
                await filesystemService.writeFileToPyodide(pyodidePath, content);
            }
            setIsVersionUpdate(false);
        }, 100);
    }, [pyodidePath]);

    useEffect(() => {
        const handleGlobalUndo = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
            }
        };

        const handleGlobalRedo = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
                e.preventDefault();
                handleRedo();
            }
        };

        window.addEventListener('keydown', handleGlobalUndo);
        window.addEventListener('keydown', handleGlobalRedo);

        return () => {
            window.removeEventListener('keydown', handleGlobalUndo);
            window.removeEventListener('keydown', handleGlobalRedo);
        };
    }, [handleUndo, handleRedo]);

    useEffect(() => {
        if (!selectedFile || appLoading) return;

        const loadFile = async () => {
            try {
                setError(null);

                const fileExtension = selectedFile.split(".").pop()?.toLowerCase() || "";

                if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "ico"].includes(fileExtension)) {
                    setFileType("image");
                    try {
                        const binaryData = await filesystemService.readFileFromPyodide(pyodidePath!);

                        if (typeof binaryData === 'string' && binaryData.startsWith('data:')) {
                            setImageSrc(binaryData);
                        } else {
                            const buffer = binaryData instanceof ArrayBuffer ? binaryData :
                                (binaryData instanceof Uint8Array ? binaryData.buffer : binaryData);
                            const blob = new Blob([buffer], { type: `image/${fileExtension}` });
                            const objectUrl = URL.createObjectURL(blob);
                            setImageSrc(objectUrl);
                        }
                    } catch (err) {
                        console.error("Error reading image file:", err);
                        setError(`Failed to load image: ${err}`);
                    }
                } else if (fileExtension === "csv") {
                    setFileType("csv");
                    try {
                        const content = await filesystemService.readFileFromPyodide(pyodidePath!);
                        setCode(content);
                    } catch (err) {
                        console.error("Error reading CSV file:", err);
                        setError(`Failed to load CSV: ${err}`);
                    }
                } else {
                    setFileType("text");
                    try {
                        const content = await filesystemService.readFileFromPyodide(pyodidePath!);

                        if (isHelloPy) {
                            if (projectStore.projectId) {
                                uiStore.setCurrentProjectId(projectStore.projectId);
                            }

                            if (uiStore.versions.length === 0) {
                                const savedContent = await uiStore.initVersioning(content, projectStore.projectId || undefined);
                                if (savedContent) {
                                    setCode(savedContent);
                                    setEditorKey(prevKey => prevKey + 1);
                                    if (pyodidePath) {
                                        await filesystemService.writeFileToPyodide(pyodidePath, savedContent);
                                    }
                                    return;
                                }
                            }
                        }

                        setCode(content);
                    } catch (err) {
                        console.error("Error reading text file:", err);
                        setError(`Failed to load file: ${err}`);

                        if (fileExtension === "py") {
                            setCode("# File could not be loaded. You can start editing from scratch.\n\n");
                        } else if (fileExtension === "md") {
                            setCode("# File could not be loaded. You can start editing from scratch.\n\n");
                        } else if (fileExtension === "html") {
                            setCode("<!-- File could not be loaded. You can start editing from scratch. -->\n<!DOCTYPE html>\n<html>\n<head>\n  <title>New Page</title>\n</head>\n<body>\n  \n</body>\n</html>");
                        } else if (fileExtension === "css") {
                            setCode("/* File could not be loaded. You can start editing from scratch. */\n\n");
                        } else if (fileExtension === "js") {
                            setCode("// File could not be loaded. You can start editing from scratch.\n\n");
                        } else if (fileExtension === "json") {
                            setCode("{\n  \n}");
                        } else {
                            setCode("");
                        }
                    }
                }

                setLanguage(getLanguageFromExtension(selectedFile));
            } catch (error) {
                console.error("Error loading file:", error);
                setError(`Error loading file: ${error instanceof Error ? error.message : String(error)}`);
                setCode("");
            }
        };

        loadFile();
    }, [selectedFile, appLoading, pyodidePath, isHelloPy, projectStore.projectId]);

    useEffect(() => {
        return () => {
            if (imageSrc && imageSrc.startsWith('blob:')) {
                URL.revokeObjectURL(imageSrc);
            }
        };
    }, [imageSrc]);

    useEffect(() => {
        const handleSaveShortcut = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key === "s") {
                event.preventDefault();
                saveFile(code);
            }
        };

        window.addEventListener("keydown", handleSaveShortcut);
        return () => window.removeEventListener("keydown", handleSaveShortcut);
    }, [code, saveFile]);

    const EditorComponent = useMemo(() => (
        <AceEditor
            key={editorKey}
            mode={language}
            theme="github"
            value={code}
            onChange={handleCodeChange}
            name={`editor-${editorKey}`}
            editorProps={{ $blockScrolling: true }}
            setOptions={editorOptions}
            width="100%"
            height="100%"
            fontSize={13}
            showPrintMargin={false}
            showGutter={false}
            highlightActiveLine={false}
            enableBasicAutocompletion={true}
            enableLiveAutocompletion={true}
            enableSnippets={true}
            wrapEnabled={true}
            style={{ width: '100%', height: '100%' }}
            onLoad={(editor) => {
                setEditor(editor);
            }}
        />
    ), [editorKey, language, code, handleCodeChange, editorOptions]);

    const TableViewerComponent = useMemo(() => (
        <TableViewer
            csvData={code}
            onDataChange={handleCodeChange}
        />
    ), [code, handleCodeChange]);

    const ErrorComponent = useMemo(() => (
        error ? (
            <div className="editor-error">
                {error}
            </div>
        ) : null
    ), [error]);

    const ContentComponent = useMemo(() => {
        if (fileType === "image" && imageSrc) {
            return <img src={imageSrc} alt="Selected file" className="editor-image" />;
        } else if (fileType === "csv") {
            return TableViewerComponent;
        } else {
            return EditorComponent;
        }
    }, [fileType, imageSrc, TableViewerComponent, EditorComponent]);

    useImperativeHandle(ref, () => ({
        refresh: refreshFile,
        undo: handleUndo,
        redo: handleRedo,
        canUndo: () => isHelloPy ? uiStore.canUndo() : false,
        canRedo: () => isHelloPy ? uiStore.canRedo() : false,
        getCurrentContent: () => code,
        updateFromVersion
    }), [refreshFile, handleUndo, handleRedo, isHelloPy, code, updateFromVersion]);

    return (
        <div className={cn("flex flex-col h-full border-r border-gray-200", className)}>
            {isHelloPy && (
                <VersionBanner
                    isVisible={uiStore.showVersionBanner}
                    hasUnsavedChanges={uiStore.hasUnsavedChangesInPreviousVersion}
                    onRestore={handleRestoreVersion}
                    onBackToLatest={handleBackToLatest}
                    onSaveChangesAsLatest={handleSaveChangesAsLatest}
                    onDiscardChanges={handleDiscardChanges}
                />
            )}
            <div className="flex flex-1 h-full">
                {showSidebar && (
                    <Sidebar
                        rootFolder={rootFolder}
                        setSelectedFile={(path) => {
                            if (path !== selectedFile) {
                                setCode("");
                                setFileType(null);
                                setImageSrc(null);
                                setLanguage("plaintext");
                                setError(null);
                                setSelectedFile(path);
                            }
                        }}
                        isCollapsed={isCollapsed}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        appLoading={appLoading}
                        isPyodideLoaded={isPyodideLoaded}
                    />
                )}
                <div className="flex-1 editor-container">
                    {selectedFile ? (
                        <>
                            {ErrorComponent}
                            <div className="editor-content relative">
                                {isGenerating && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-50">
                                        <div className="flex flex-col items-center gap-3 p-4 bg-white rounded-lg shadow-lg border border-gray-200">
                                            <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-300 border-t-gray-900"></div>
                                            <p className="text-sm font-medium text-gray-700">Generating quick start app...</p>
                                        </div>
                                    </div>
                                )}
                                {ContentComponent}
                            </div>
                        </>
                    ) : (
                        <div className="editor-empty">
                            <p className="editor-empty-text">No file selected</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}));

CodeEditor.displayName = 'CodeEditor';

export default CodeEditor;